import { getPositionKey } from "domain/synthetics/positions/utils";
import { OrderInfo, OrderParams, OrdersInfoData } from "sdk/types/orders";
import { PositionsInfoData } from "sdk/types/positions";
import {
  isLimitIncreaseOrderType,
  isOrderForPosition,
  isTriggerDecreaseOrderType,
  isTwapOrder,
} from "sdk/utils/orders";

type OrderLike = OrderInfo | OrderParams;

function resolveOrderInfo(order: OrderLike, ordersInfoData: OrdersInfoData | undefined) {
  if ("isSwap" in order) {
    return order;
  }

  if (order.isTwap) {
    return undefined;
  }

  return ordersInfoData?.[order.key];
}

function getOrderKeysForCancel(order: OrderLike) {
  return order.isTwap ? order.orders.map((twapOrder) => twapOrder.key) : [order.key];
}

function getPositionKeyForLimitIncreaseOrder(order: OrderInfo) {
  if (isTwapOrder(order) || !isLimitIncreaseOrderType(order.orderType) || order.isSwap) {
    return undefined;
  }

  const collateralAddress = order.targetCollateralToken.isNative
    ? order.targetCollateralToken.wrappedAddress
    : order.targetCollateralToken.address;

  if (!collateralAddress) {
    return undefined;
  }

  return getPositionKey(order.account, order.marketAddress, collateralAddress, order.isLong);
}

export function getExpandedCancelOrderKeys(
  order: OrderLike,
  ordersInfoData: OrdersInfoData | undefined,
  positionsInfoData: PositionsInfoData | undefined
) {
  const expandedKeys = new Set(getOrderKeysForCancel(order));
  const resolvedOrder = resolveOrderInfo(order, ordersInfoData);
  const positionKey = resolvedOrder ? getPositionKeyForLimitIncreaseOrder(resolvedOrder) : undefined;

  if (!positionKey || !ordersInfoData) {
    return Array.from(expandedKeys);
  }

  const resolvedPositionKey = positionKey;

  if (positionsInfoData?.[resolvedPositionKey]) {
    return Array.from(expandedKeys);
  }

  Object.values(ordersInfoData).forEach((candidateOrder) => {
    if (isTwapOrder(candidateOrder) || !isTriggerDecreaseOrderType(candidateOrder.orderType)) {
      return;
    }

    if (isOrderForPosition(candidateOrder, resolvedPositionKey)) {
      expandedKeys.add(candidateOrder.key);
    }
  });

  return Array.from(expandedKeys);
}

export function getExpandedCancelOrderKeysForOrders(
  orders: OrderLike[],
  ordersInfoData: OrdersInfoData | undefined,
  positionsInfoData: PositionsInfoData | undefined
) {
  const expandedKeys = new Set<string>();

  orders.forEach((order) => {
    getExpandedCancelOrderKeys(order, ordersInfoData, positionsInfoData).forEach((orderKey) =>
      expandedKeys.add(orderKey)
    );
  });

  return Array.from(expandedKeys);
}
